import { IUnitSchedule, Schedule } from "@auvious/utils";
import {
  Component,
  ComponentInterface,
  h,
  Event,
  EventEmitter,
  State,
  Prop,
  Watch,
} from "@stencil/core";
import { IThemeOptions, ISelectOption } from "../../../models";
import { AppConfigService, ThemeService } from "../../../services";

enum Meridian {
  AM,
  PM,
}

interface ITimePickerState {
  meridian?: Meridian;
  time?: Date;
}

const DEFAULT_TIME_PICKER_STATE: ITimePickerState = {
  meridian: Meridian.AM,
  time: null,
};

@Component({
  tag: "av-time-picker",
  styleUrl: "av-time-picker.scss",
  shadow: true,
})
export class AvTimePicker implements ComponentInterface {
  @Prop() time: Date = new Date();
  @Prop() schedule: Schedule;

  @State() state = { ...DEFAULT_TIME_PICKER_STATE };
  @State() themeOptions: IThemeOptions;

  @Event() timeChange: EventEmitter<Date>;

  hourOptions: ISelectOption[];
  minuteOptions: ISelectOption[];

  selectedHour: ISelectOption;
  selectedMinute: ISelectOption;

  // explicitly selected time which should override input date
  private userSelectedHour: number;
  private userSelectedMinute: number;

  private hoursOpen: readonly IUnitSchedule[];
  private minutesOpen: readonly IUnitSchedule[];

  private step = AppConfigService.scheduleIntervalMinutes;

  constructor() {
    ThemeService.optionsAvailable().subscribe(
      (options) => (this.themeOptions = options)
    );

    this.minuteOptions = [];
    for (
      let minute = 0;
      minute < 60;
      minute += AppConfigService.scheduleIntervalMinutes
    ) {
      this.minuteOptions.push({
        label: this.pad(minute),
        value: String(minute),
        disabled: true,
      });
    }
    // this.minuteOptions = [
    //   { label: "00", value: "0", disabled: true },
    //   { label: "15", value: "15", disabled: true },
    //   { label: "30", value: "30", disabled: true },
    //   { label: "45", value: "45", disabled: true },
    // ];

    this.hourOptions = [
      {
        label: String(12),
        value: String(12),
        disabled: true,
      },
    ];

    for (let i = 1; i < 12; i++) {
      this.hourOptions.push({
        label: String(i),
        value: String(i),
        disabled: true,
      });
    }
  }

  @Watch("time")
  timeChanged(date: Date) {
    if (date && date.getTime() !== this.state.time.getTime()) {
      this.updateOpenTime(date);
      this.state.time = date;
    }
  }

  @Watch("schedule")
  scheduleChanged(value) {
    if (value) {
      this.updateOpenTime(this.state.time);
    }
  }

  private dateParts(date: Date) {
    return [
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
    ];
  }

  private pad(val) {
    const valString = val + "";
    return valString.length < 2 ? "0" + valString : valString;
  }

  private closestAvailableDayTime(date: Date) {
    const [year, month, day] = this.dateParts(date);
    const hours = this.userSelectedHour;
    const minutes = this.userSelectedMinute;

    const next = this.schedule.nextOpenTime(
      year,
      month,
      day,
      hours,
      minutes,
      this.step
    );

    let previous = this.schedule.previousOpenTime(
      year,
      month,
      day,
      hours,
      minutes,
      this.step
    );

    // at least a step from now
    if (
      previous &&
      Date.now() + this.step * 60 * 1000 >
        new Date(year, month, day, previous.hour, previous.minute).getTime()
    ) {
      previous = null;
    }

    const time = !next
      ? previous
      : !previous
      ? next
      : hours * 60 + minutes - (previous.hour * 60 + previous.minute) <
        next.hour * 60 + next.minute - (hours * 60 + minutes)
      ? previous
      : next;

    return time ? new Date(year, month, day, time.hour, time.minute) : null;
  }

  componentWillLoad() {
    const [year, month, day, hours, minutes] = this.dateParts(this.time);
    const time = this.schedule.nextOpenTime(
      year,
      month,
      day,
      hours,
      minutes,
      this.step
    );

    this.userSelectedHour = time?.hour ?? hours;
    this.userSelectedMinute = time?.minute ?? minutes;

    this.selectedHour = this.hourOptions[this.userSelectedHour % 12];
    this.selectedMinute = this.minuteOptions.find(
      (m) => Number(m.value) === this.userSelectedMinute
    );

    this.time.setHours(this.userSelectedHour);
    this.time.setMinutes(this.userSelectedMinute);

    this.setState({
      ...this.state,
      meridian: this.userSelectedHour < 12 ? Meridian.AM : Meridian.PM,
      time: this.time,
    });

    this.updateOpenTime(this.state.time);
  }

  private updateOpenTime(date: Date) {
    const [year, month, day, hours] = this.dateParts(date);
    const [yearNow, monthNow, dayNow, hoursNow, minNow] = this.dateParts(
      new Date()
    );
    const isToday = yearNow === year && monthNow === month && dayNow === day;
    const isHourNow = isToday && hours === hoursNow;

    this.hoursOpen = this.schedule.getHours(year, month, day);

    const offset = date.getHours() < 12 ? 0 : 12;
    this.hourOptions = this.hourOptions.map((hour, i) => ({
      ...hour,
      disabled:
        this.hoursOpen[i + offset]?.open === false ||
        (isToday && i + offset < hoursNow),
    }));

    this.minutesOpen = this.schedule.getMinutes(year, month, day, hours);

    this.minuteOptions = this.minuteOptions.map((minute) => ({
      ...minute,
      disabled:
        this.minutesOpen[+minute.value]?.open === false ||
        (isHourNow && +minute.value < minNow),
    }));
  }

  meridianChange(value: Meridian) {
    if (this.state.meridian !== value) {
      const [year, month, day] = this.dateParts(this.state.time);

      // check if schedule allows it
      const isAM = value === Meridian.AM;
      let date: Date;

      if (isAM && this.userSelectedHour < 12) {
        date = this.closestAvailableDayTime(
          new Date(
            year,
            month,
            day,
            this.userSelectedHour,
            this.userSelectedMinute
          )
        );

        if (!date || date.getHours() >= 12) {
          return;
        }
      } else if (!isAM && this.userSelectedHour >= 12) {
        date = this.closestAvailableDayTime(
          new Date(
            year,
            month,
            day,
            this.userSelectedHour,
            this.userSelectedMinute
          )
        );

        if (!date || date.getHours() < 12) {
          return;
        }
      } else {
        const time = isAM
          ? this.schedule.previousOpenTime(year, month, day, 12, 0, this.step)
          : this.schedule.nextOpenTime(year, month, day, 12, 0, this.step);

        date = time ? new Date(year, month, day, time.hour, time.minute) : null;
      }

      if (!date || Date.now() > date.getTime()) {
        return;
      }

      this.selectedHour = this.hourOptions[date.getHours() % 12];
      this.selectedMinute = this.minuteOptions.find(
        (m) => Number(m.value) === date.getMinutes()
      );

      this.setState({
        ...this.state,
        meridian: value,
        time: date,
      });

      this.updateOpenTime(this.state.time);
    }
  }

  hourChanged({ detail: hour }: CustomEvent<ISelectOption>) {
    this.userSelectedHour =
      (+hour.value % 12) + (this.state.meridian === Meridian.PM ? 12 : 0);

    this.setState({
      time: new Date(this.state.time.setHours(this.userSelectedHour)),
    });

    this.updateOpenTime(this.state.time);
  }

  minuteChanged({ detail: minute }: CustomEvent<ISelectOption>) {
    this.userSelectedMinute = +minute.value;
    this.setState({
      time: new Date(this.state.time.setMinutes(this.userSelectedMinute)),
    });
  }

  setState(state: ITimePickerState) {
    this.state = { ...this.state, ...state };
    this.timeChange.emit(this.state.time);
  }

  render() {
    const optionStyle = {
      backgroundColor: this.themeOptions.colorAccent,
    };

    return (
      <div
        class={{
          "av-time-picker": true,
          "av-time-picker-dark": this.themeOptions.darkMode,
        }}
      >
        <div class="av-time-picker-input">
          <av-select
            data-tid="time-picker/hour"
            selected={this.selectedHour}
            options={this.hourOptions}
            onChangeComplete={this.hourChanged.bind(this)}
          ></av-select>
        </div>
        <div>:</div>
        <div class="av-time-picker-input">
          <av-select
            data-tid="time-picker/minute"
            selected={this.selectedMinute}
            options={this.minuteOptions}
            onChangeComplete={this.minuteChanged.bind(this)}
          ></av-select>
        </div>
        <div class="av-time-picker-meridian">
          <button
            class={{ active: this.state.meridian === Meridian.AM }}
            style={this.state.meridian === Meridian.AM && optionStyle}
            onClick={() => this.meridianChange(Meridian.AM)}
          >
            <av-translate>AM</av-translate>
          </button>
          <button
            class={{ active: this.state.meridian === Meridian.PM }}
            style={this.state.meridian === Meridian.PM && optionStyle}
            onClick={() => this.meridianChange(Meridian.PM)}
          >
            <av-translate>PM</av-translate>
          </button>
        </div>
      </div>
    );
  }
}
